/**
 * Input Sanitization Utility
 *
 * SECURITY FIX: Prevents XSS attacks by stripping HTML tags from user input.
 * Uses a small DOMParser-based sanitizer (no external dependency).
 *
 * Referenced Vulnerability: #17 - Improper Input Validation (HTML tags accepted)
 */

const DEFAULT_ALLOWED_TAGS = new Set(['B', 'STRONG', 'I', 'EM', 'U', 'BR', 'P', 'UL', 'OL', 'LI']);

const stripAllHtml = (input: string): string => {
	// Server-side/test environments: fall back to a conservative strip
	if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
		return input.replace(/<[^>]*>/g, '');
	}
	const doc = new DOMParser().parseFromString(input, 'text/html');
	return doc.body.textContent ?? '';
};

const sanitizeWithAllowlist = (
	input: string,
	allowedTags: Set<string> = DEFAULT_ALLOWED_TAGS,
): string => {
	if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
		// No DOM available: safest fallback is plain text.
		return stripAllHtml(input);
	}

	const doc = new DOMParser().parseFromString(input, 'text/html');

	const walk = (node: Node) => {
		if (node.nodeType === Node.COMMENT_NODE) {
			node.parentNode?.removeChild(node);
			return;
		}

		if (node.nodeType === Node.ELEMENT_NODE) {
			const el = node as HTMLElement;
			const tag = el.tagName.toUpperCase();

			// Drop disallowed tags but keep their text content
			if (!allowedTags.has(tag)) {
				const parent = el.parentNode;
				if (!parent) return;
				while (el.firstChild) parent.insertBefore(el.firstChild, el);
				parent.removeChild(el);
				return;
			}

			// Remove all attributes to prevent `on*`, `style`, `href="javascript:"`, etc.
			for (const attr of Array.from(el.attributes)) {
				el.removeAttribute(attr.name);
			}
		}

		for (const child of Array.from(node.childNodes)) {
			walk(child);
		}
	};

	walk(doc.body);
	return doc.body.innerHTML;
};

/**
 * Strips all HTML tags from a string, returning only plain text.
 * Useful for sanitizing form inputs before storing or displaying.
 *
 * @param input - The raw input string that may contain HTML
 * @returns Clean plain-text string with all HTML removed
 */
export const stripHtmlTags = (input: string): string => {
	if (!input || typeof input !== 'string') return '';
	return stripAllHtml(input);
};

/**
 * Sanitizes input while allowing safe formatting tags (bold, italic, etc.).
 * Use this for rich-text fields where some formatting is acceptable.
 * @param input - The raw input string
 * @returns Sanitized HTML with only safe tags allowed
 */
export const sanitizeHtml = (input: string): string => {
	if (!input || typeof input !== 'string') return '';
	return sanitizeWithAllowlist(input);
};

export default stripHtmlTags;
